import { Container, Graphics, type Texture } from 'pixi.js';
import type { LoadedArena } from '../arenas/types';
import type { Actor, PlayerSummary } from '../battle/types';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../data/constants';
import type { CharacterTimings } from '../characters/types';
import type { CharacterParticles } from './assets';
import {
  CharacterSprite,
  type CharacterAnimations,
  type CharacterSfx,
} from './CharacterSprite';
import { spawnFloatingText } from './FloatingText';
import { HPBar } from './HPBar';
import { spawnGearBurst } from './Particles';

const BASELINE_OFFSET_FROM_BOTTOM = 50;
const SPRITE_GAP = 450;

const HP_BAR_WIDTH = 280;
const HP_BAR_HEIGHT = 16;
const HP_BAR_MARGIN = 16;
const HP_BAR_TOP = 24;

const FX_LAYER_BASELINE_OFFSET = 8;

// Action log columns - p1 hugs the left edge, p2 hugs the right.
// Text spawns at LOG_BASE_Y and rises.
const LOG_EDGE_MARGIN = 12;
const LOG_BASE_Y = DESIGN_HEIGHT - 80;
const LOG_RISE = 90;
const LOG_DURATION_SEC = 1.0;
const LOG_FONT_SIZE = 11;
// Skill icon shown right under the log label (skills only).
const LOG_ICON_SIZE = 42;
const LOG_ICON_GAP = 4;
const LOG_LETTER_SPACING = 2.5;
const LOG_DROP_SHADOW = {
  color: 0x000000,
  alpha: 1,
  angle: Math.PI / 4,
  distance: 2,
  blur: 0,
} as const;

export type BattleSceneOptions = {
  players: Record<Actor, PlayerSummary>;
  // Density at which Pixi Text bitmaps are baked. Higher = sharper when stage
  // is scaled up, more memory. Computed in main.ts from window+DPR+mode.
  textResolution: number;
  // Optional arena bundle (background + decoration mount fn). If absent, scene
  // falls back to a plain dark ground strip (placeholder).
  arena?: LoadedArena;
  // Optional per-actor animation frame sets (idle, block, ...). If provided
  // for an actor, their CharacterSprite uses AnimatedSprite instead of the
  // colored placeholder rect.
  characterAnimations?: Partial<Record<Actor, CharacterAnimations>>;
  // Optional per-actor SFX clips (e.g. pain sound on hit). Played by the
  // adapter alongside the matching animation.
  characterSfx?: Partial<Record<Actor, CharacterSfx>>;
  // Optional per-actor animation timings (frame speeds, impact frames, hold
  // durations). Adapter reads these via CharacterSprite.timings.
  characterTimings?: Partial<Record<Actor, CharacterTimings>>;
  // Optional per-actor particle textures (e.g. gears for damage). Scene fires
  // them on demand from the renderer (e.g. spawnDamageParticles).
  characterParticles?: Partial<Record<Actor, CharacterParticles>>;
  // Optional font family for character name labels above HP bars. Must already
  // be loaded (e.g. via Pixi Assets.load) before scene construction.
  titleFontFamily?: string;
};

export class BattleScene extends Container {
  private readonly p1: CharacterSprite;
  private readonly p2: CharacterSprite;
  private readonly p1HpBar: HPBar;
  private readonly p2HpBar: HPBar;
  private readonly particleLayer: Container;
  private readonly fxLayer: Container;
  private readonly textResolution: number;
  private readonly titleFontFamily: string | undefined;
  private readonly particles: Partial<Record<Actor, CharacterParticles>>;
  private readonly arena: LoadedArena | undefined;

  constructor(opts: BattleSceneOptions) {
    super();
    this.textResolution = opts.textResolution;
    this.titleFontFamily = opts.titleFontFamily;
    this.particles = opts.characterParticles ?? {};
    this.arena = opts.arena;

    // Battle stays letterboxed to the design rect. The canvas now covers the
    // whole window (desktop screen paints past the rect), so the scene crops
    // itself - everything outside shows the dark canvas background (the bars).
    const frameMask = new Graphics().rect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT).fill(0xffffff);
    this.addChild(frameMask);
    this.mask = frameMask;

    if (opts.arena) {
      // Arena owns its full visual composition (bg layers + decorations + fx).
      // BattleScene just hands it a Container; the bundle's mount() paints it.
      // Sits below characters so attackers visibly pass IN FRONT of wall stuff.
      const arenaLayer = new Container();
      this.addChild(arenaLayer);
      opts.arena.bundle.mount({
        layer: arenaLayer,
        sheets: opts.arena.sheets,
        textures: opts.arena.textures,
      });
    } else {
      const ground = new Graphics()
        .rect(
          0,
          DESIGN_HEIGHT - BASELINE_OFFSET_FROM_BOTTOM,
          DESIGN_WIDTH,
          BASELINE_OFFSET_FROM_BOTTOM,
        )
        .fill(0x222222);
      this.addChild(ground);
    }

    this.p1 = new CharacterSprite({
      color: 0x4a90e2,
      facing: 1,
      animations: opts.characterAnimations?.p1,
      sfx: opts.characterSfx?.p1,
      timings: opts.characterTimings?.p1,
    });
    this.p2 = new CharacterSprite({
      color: 0xeab308,
      facing: -1,
      animations: opts.characterAnimations?.p2,
      sfx: opts.characterSfx?.p2,
      timings: opts.characterTimings?.p2,
    });

    const baseline = DESIGN_HEIGHT - BASELINE_OFFSET_FROM_BOTTOM;
    const cx = DESIGN_WIDTH / 2;
    this.p1.setHomePosition(cx - SPRITE_GAP / 2, baseline);
    this.p2.setHomePosition(cx + SPRITE_GAP / 2, baseline);
    this.addChild(this.p1, this.p2);

    this.p1HpBar = new HPBar({
      width: HP_BAR_WIDTH,
      height: HP_BAR_HEIGHT,
      maxHP: opts.players.p1.maxHP,
      name: opts.players.p1.name,
      textResolution: this.textResolution,
      fontFamily: opts.titleFontFamily,
    });
    this.p1HpBar.position.set(HP_BAR_MARGIN, HP_BAR_TOP);

    this.p2HpBar = new HPBar({
      width: HP_BAR_WIDTH,
      height: HP_BAR_HEIGHT,
      maxHP: opts.players.p2.maxHP,
      name: opts.players.p2.name,
      alignRight: true,
      textResolution: this.textResolution,
      fontFamily: opts.titleFontFamily,
    });
    this.p2HpBar.position.set(DESIGN_WIDTH - HP_BAR_MARGIN - HP_BAR_WIDTH, HP_BAR_TOP);

    // Particle layer sits in front of characters (so debris isn't hidden
    // behind them) but behind HP bars and floating text.
    this.particleLayer = new Container();
    this.addChild(this.particleLayer);

    this.addChild(this.p1HpBar, this.p2HpBar);

    // Floating text layer is on top of everything.
    this.fxLayer = new Container();
    this.addChild(this.fxLayer);
  }

  getSprite(actor: Actor): CharacterSprite {
    return actor === 'p1' ? this.p1 : this.p2;
  }

  // Loaded arena bundle + assets. Renderer uses it to route arena_* events to
  // the bundle's playArenaEvent hook. Undefined when scene was built without
  // an arena (placeholder ground mode).
  getArena(): LoadedArena | undefined {
    return this.arena;
  }

  // Adds an arbitrary fx Container (e.g. skill projectile, ad-hoc effect) to
  // the particle layer so it renders above characters but below HP/text.
  spawnEffect(child: Container): void {
    this.particleLayer.addChild(child);
  }

  // Ensures `actor`'s sprite renders ABOVE the other character. Used so the
  // current attacker (who lunges into the defender) is in front instead of
  // disappearing behind. Particles/HP/fx layers were added later so they stay
  // on top regardless - we only swap the two character sprites.
  bringToFront(actor: Actor): void {
    const me = this.getSprite(actor);
    const other = this.getSprite(actor === 'p1' ? 'p2' : 'p1');
    const myIdx = this.getChildIndex(me);
    const otherIdx = this.getChildIndex(other);
    if (myIdx < otherIdx) {
      this.setChildIndex(me, otherIdx);
    }
  }

  setHP(actor: Actor, currentHP: number): void {
    const bar = actor === 'p1' ? this.p1HpBar : this.p2HpBar;
    bar.setCurrentHP(currentHP);
  }

  // Damage numbers etc - spawn above the character's head.
  spawnHeadText(actor: Actor, text: string, color: number): void {
    const sprite = this.getSprite(actor);
    spawnFloatingText(this.fxLayer, {
      text,
      color,
      x: sprite.x,
      y: sprite.topY - FX_LAYER_BASELINE_OFFSET,
      resolution: this.textResolution,
    });
  }

  // Bursts gear/debris particles from the target's body, falling to the ground.
  // `fromActor` is whoever struck the blow - used to bias debris direction so
  // particles fly away from the punch instead of into it.
  spawnDamageParticles(target: Actor, fromActor: Actor): void {
    const particles = this.particles[target]?.damage;
    if (!particles || particles.length === 0) return;
    const targetSprite = this.getSprite(target);
    const attackerSprite = this.getSprite(fromActor);
    const bias = Math.sign(targetSprite.x - attackerSprite.x);
    spawnGearBurst({
      layer: this.particleLayer,
      particles,
      x: targetSprite.x,
      y: targetSprite.y - targetSprite.bodyHeight / 2,
      groundY: targetSprite.baseY,
      directionBias: bias as -1 | 0 | 1,
    });
  }

  // Action / reaction labels - spawn at the screen edge, p1 left, p2 right.
  // iconTexture (the firing skill's own logIcon, if any) is shown under the
  // label as one block; omit it for plain text labels.
  spawnActionLog(actor: Actor, text: string, color: number, iconTexture?: Texture): void {
    const isLeft = actor === 'p1';
    const icon = iconTexture
      ? { texture: iconTexture, size: LOG_ICON_SIZE, gap: LOG_ICON_GAP }
      : undefined;
    spawnFloatingText(this.fxLayer, {
      text,
      color,
      x: isLeft ? LOG_EDGE_MARGIN : DESIGN_WIDTH - LOG_EDGE_MARGIN,
      y: LOG_BASE_Y,
      align: isLeft ? 'left' : 'right',
      fontSize: LOG_FONT_SIZE,
      riseBy: LOG_RISE,
      durationSec: LOG_DURATION_SEC,
      resolution: this.textResolution,
      fontFamily: this.titleFontFamily,
      letterSpacing: LOG_LETTER_SPACING,
      dropShadow: LOG_DROP_SHADOW,
      ...(icon ? { icon } : {}),
    });
  }
}
